import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  SiteAddApiModel,
  SiteApiModel,
  SiteProgramsActiveStatusApiModel,
  CheckSiteTierComplianceApiModel,
  DeletedSiteApiModel
} from '@app/api/api-models';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import { UploadFileService } from '@app/shared/services';
import { ImportResultApiModel } from '../api-models/ImportResultApiModel';

@Injectable()
export class SitesService {
  public apiUrl = 'sites';
  private refreshSubject = new Subject<void>();

  constructor(private http: HttpClientService, private readonly uploadFileService: UploadFileService) {}

  public getList(): Observable<Array<SiteApiModel>> {
    return this.http
      .get<Array<SiteApiModel>>(`${this.apiUrl}/get-list`)
      .pipe(map((array) => array.map((x) => new SiteApiModel().deserialize(x))));
  }

  public getInactiveList(): Observable<Array<SiteApiModel>> {
    return this.http
      .get<Array<SiteApiModel>>(`${this.apiUrl}/get-inactive`)
      .pipe(map((array) => array.map((x) => new SiteApiModel().deserialize(x))));
  }

  public getDeletedList(): Observable<Array<DeletedSiteApiModel>> {
    return this.http.get<Array<DeletedSiteApiModel>>(`${this.apiUrl}/get-deleted`);
  }

  public add(site: SiteAddApiModel): Observable<SiteApiModel> {
    return this.http
      .post<SiteApiModel>(`${this.apiUrl}/create`, site)
      .pipe(map((s) => new SiteApiModel().deserialize(s)));
  }

  public update(site: SiteApiModel): Observable<SiteApiModel> {
    return this.http
      .put<SiteApiModel>(`${this.apiUrl}/update`, site)
      .pipe(map((s) => new SiteApiModel().deserialize(s)));
  }

  public delete(siteId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/delete/${siteId}`);
  }

  public checkSiteCompliance(site: SiteApiModel): Observable<SiteProgramsActiveStatusApiModel> {
    const model = new CheckSiteTierComplianceApiModel();
    model.SiteId = site.Id;
    model.SiteTier = site.Tier;
    return this.http.put<SiteProgramsActiveStatusApiModel>(`${this.apiUrl}/check-site-compliance`, model);
  }

  public updateSiteActivePrograms(sitePrograms: SiteProgramsActiveStatusApiModel): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/update-site-programs-status`, sitePrograms);
  }

  public getImportTemplateExample(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/get-import-template-example`, { responseType: 'blob' });
  }

  public importXlsx(file: File): Observable<ImportResultApiModel> {
    const payload = new FormData();
    this.uploadFileService.append(payload, file);

    return this.http.post(`${this.apiUrl}/import-xlsx`, payload, { responseType: 'blob', observe: 'response' }).pipe(
      map((data: any) => {
        const model = new ImportResultApiModel();
        model.Blob = data.body;

        if (data.headers.has('Import-Results')) {
          const value = data.headers.get('Import-Results');
          model.FailedCount = +value.split(';')[0];
          model.ImportedCount = +value.split(';')[1];
        }

        return model;
      })
    );
  }

  public get needToRefresh(): Observable<void> {
    return this.refreshSubject.asObservable();
  }

  public handleRefresh() {
    this.refreshSubject.next();
  }
}
